import React from "react";
import { motion } from "framer-motion";
import { ArrowRight, ShoppingCart, Check, Sparkles, BookOpen, MessageCircle } from "lucide-react";
import PhoneAnchor from "./PhoneAnchor";

export default function StageHero({
  onContinue,
  enabledServices,
  hostLogo,
  compactHeight = false,
}) {
  return (
    <div
      className={`relative w-full h-full grid laptop:grid-rows-1 laptop:grid-cols-[1.1fr_0.9fr] ${
        compactHeight
          ? "grid-rows-[minmax(0,34%)_minmax(0,66%)]"
          : "grid-rows-[minmax(0,48%)_minmax(0,52%)]"
      }`}
    >
      {/* Left — copy */}
      <div
        className={`relative flex items-center px-6 laptop:px-14 order-2 laptop:order-1 min-h-0 overflow-y-auto ${
          compactHeight ? "py-2 laptop:py-3 laptop:overflow-y-auto" : "py-4 laptop:py-0 laptop:overflow-visible"
        }`}
      >
        <div className="w-full max-w-[560px] mx-auto laptop:mx-0">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#F7F7F8] border border-[#EAEAEA] ${
              compactHeight ? "mb-3" : "mb-5"
            }`}
          >
            <span className="w-1.5 h-1.5 rounded-full bg-[#EF4949]" />
            <p className="text-[9px] font-bold uppercase tracking-[0.22em] text-[#888888]">
              A guided 60-second tour
            </p>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, delay: 0.15 }}
            className="text-[30px] tabletOnly:text-[34px] laptop:text-[44px] font-extrabold text-[#111111] leading-[1.04]"
            style={{ letterSpacing: -0.6 }}
          >
            Turn every stay into a
            <br />
            5-star experience.
            <br />
            <span className="text-[#EF4949]">Earn on every extra.</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.25 }}
            className={`text-[14px] laptop:text-[15px] text-[#5F6368] leading-relaxed max-w-[460px] ${
              compactHeight ? "mt-3" : "mt-4"
            }`}
          >
            Bellhopt Concierge gives your rentals a branded, guest-facing
            concierge desk. Guests get a guidebook, pre-arrival groceries, and
            AI-assisted intake, while Bellhopt's live concierge team handles the
            real requests, curation, and logistics behind the scenes.
          </motion.p>

          {/* Included-free strip — platform features every subscription gets */}
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.35 }}
            className={compactHeight ? "mt-4" : "mt-6"}
          >
            <div className="inline-flex items-center gap-1.5 mb-2">
              <Check size={10} className="text-[#10B981]" strokeWidth={3} />
              <span className="text-[9px] font-bold uppercase tracking-[0.22em] text-[#10B981]">
                Always included
              </span>
            </div>
            <div className="flex flex-wrap items-center gap-1.5">
              <IncludedPill
                icon={<ShoppingCart size={12} className="text-[#10B981]" strokeWidth={2.2} />}
                label="Pre-Arrival Groceries"
              />
              <IncludedPill
                icon={<BookOpen size={12} className="text-[#10B981]" strokeWidth={2.2} />}
                label="Branded Guidebook"
              />
              <IncludedPill
                icon={<MessageCircle size={12} className="text-[#10B981]" strokeWidth={2.2} />}
                label="AI + Human Concierge"
              />
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.45 }}
            className={`flex items-center gap-3 ${compactHeight ? "mt-5" : "mt-8"}`}
          >
            <button
              onClick={onContinue}
              className="inline-flex items-center gap-2 px-6 py-3.5 bg-[#111111] text-white text-[14px] font-bold rounded-[12px] hover:bg-black transition-colors"
            >
              See how it works
              <ArrowRight size={14} strokeWidth={2.5} />
            </button>
            <span className="hidden tabletOnly:inline laptop:inline text-[11px] text-[#888888]">
              <kbd className="inline-flex items-center justify-center w-5 h-5 rounded border border-[#EAEAEA] bg-white text-[10px] font-semibold text-[#5F6368] mx-1">→</kbd>
              or arrow keys
            </span>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            className={`grid grid-cols-3 gap-x-8 gap-y-1 max-w-[440px] ${
              compactHeight ? "mt-5" : "mt-8"
            }`}
          >
            {[
              { k: "5-star", v: "Guest-facing support" },
              { k: "$25–$100", v: "Early/late stay revenue" },
              { k: "Your price", v: "On custom services" },
            ].map((m) => (
              <div key={m.k} className="flex flex-col">
                <span className="text-[17px] laptop:text-[19px] font-extrabold text-[#111111] leading-tight" style={{ letterSpacing: -0.4 }}>
                  {m.k}
                </span>
                <span className="text-[9px] font-bold uppercase tracking-[0.16em] text-[#888888] mt-1">
                  {m.v}
                </span>
              </div>
            ))}
          </motion.div>
        </div>
      </div>

      {/* Right — phone */}
      <div className="relative order-1 laptop:order-2 min-h-0 overflow-hidden">
        {/* Decorative background */}
        <div
          aria-hidden
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(500px 400px at 60% 40%, rgba(239,73,73,0.08), transparent 60%)",
          }}
        />
        <div
          className={`relative h-full flex items-center justify-center ${
            compactHeight ? "py-2 laptop:py-4" : "py-4 laptop:py-10"
          }`}
        >
          <PhoneAnchor
            stage={1}
            enabledServices={enabledServices}
            hostLogo={hostLogo}
            compactHeight={compactHeight}
          />
        </div>

        {/* Floating hint chip */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.8, duration: 0.6 }}
          className="hidden laptop:flex absolute top-1/2 -translate-y-1/2 right-8 items-center gap-2 px-3 py-2 rounded-full bg-white border border-[#EAEAEA] shadow-[0_10px_30px_rgba(0,0,0,0.06)]"
        >
          <Sparkles size={12} className="text-[#EF4949]" />
          <span className="text-[10px] font-semibold text-[#111111]">This is what guests see</span>
        </motion.div>
      </div>
    </div>
  );
}

function IncludedPill({ icon, label }) {
  return (
    <span className="inline-flex items-center gap-1.5 pl-1.5 pr-2.5 py-1.5 rounded-full bg-white border border-[#EAEAEA] shadow-[0_6px_20px_rgba(0,0,0,0.03)]">
      <span className="w-5 h-5 rounded-full bg-[#ECFDF5] flex items-center justify-center">
        {icon}
      </span>
      <span className="text-[11px] font-semibold text-[#111111] leading-none">
        {label}
      </span>
    </span>
  );
}
